import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography
} from '@mui/material';
import { apiService } from '../services/api';
import { useFormFieldErrors } from '@/hooks/useFormFieldErrors';
import NfseCommonFields from '../components/nfse/NfseCommonFields';
import NfseIpmFields from '../components/nfse/NfseIpmFields';
import NfseNacionalFields, { type NfseNacionalStatus } from '../components/nfse/NfseNacionalFields';
import {
  buildFiscalPayload,
  emptyFiscalForm,
  formFromConfig,
  type FiscalFormState
} from '../components/nfse/nfseFiscalForm';

const PROVIDER_LABEL: Record<string, string> = { ipm: 'IPM', nacional: 'NFS-e Nacional' };

const NfseFiscalConfigPage: React.FC = () => {
  const [form, setForm] = useState<FiscalFormState>(emptyFiscalForm);
  /** Resposta crua de GET/PUT /billing/config (inclui `nfseNacionalStatus`). */
  const [rawConfig, setRawConfig] = useState<Record<string, unknown>>({});
  const [ipmPasswordConfigured, setIpmPasswordConfigured] = useState(false);
  /** Só envia ipmPassword ao billing se o usuário digitou algo após o carregamento (evita autofill / lixo). */
  const [ipmPasswordDirty, setIpmPasswordDirty] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [activationBlocked, setActivationBlocked] = useState<string[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { fieldErrors, setFieldErrors, clearFieldErrors, clearField, applyApiError } =
    useFormFieldErrors();

  const applyLoaded = (data: Record<string, unknown>) => {
    setRawConfig(data);
    setIpmPasswordConfigured(Boolean(data.ipmPasswordConfigured));
    setIpmPasswordDirty(false);
    setForm(formFromConfig(data));
  };

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await apiService.getBillingGlobalConfig();
        applyLoaded((res.data || {}) as Record<string, unknown>);
      } catch (e: unknown) {
        setError(
          (e as { response?: { data?: { message?: string } } })?.response?.data?.message ||
            (e as Error)?.message ||
            'Falha ao carregar configuração fiscal.'
        );
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const onChange =
    (key: keyof FiscalFormState) =>
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      clearField(key);
      setForm(prev => ({ ...prev, [key]: event.target.value }));
    };

  const onSave = async () => {
    setSaving(true);
    setMessage(null);
    setActivationBlocked(null);
    setError(null);
    clearFieldErrors();

    const built = buildFiscalPayload(form, { ipmPasswordDirty });
    if ('errors' in built) {
      setFieldErrors(built.errors);
      setSaving(false);
      return;
    }

    try {
      const res = await apiService.updateBillingGlobalConfig(built.payload);
      const saved = (res.data || {}) as Record<string, unknown>;
      applyLoaded(saved);
      const blocked = saved.nfseActivationBlocked as { missing?: string[] } | undefined;
      if (blocked) {
        // Dados salvos, mas o billing recusou ativar o Nacional: mantém a tela no formulário que o usuário editava
        // e destaca (alerta + campos) o que falta. Provedor/data NÃO foram gravados.
        setForm(prev => ({
          ...prev,
          nfseProvider: form.nfseProvider,
          nfseNacionalStartDate: form.nfseNacionalStartDate
        }));
        setActivationBlocked(blocked.missing ?? []);
      } else {
        setMessage('Configurações fiscais salvas com sucesso.');
      }
    } catch (e: unknown) {
      setError(applyApiError(e, 'Falha ao salvar configuração fiscal.'));
    } finally {
      setSaving(false);
    }
  };

  const status = (rawConfig.nfseNacionalStatus || {}) as NfseNacionalStatus;
  const view: 'ipm' | 'nacional' = form.nfseProvider === 'nacional' ? 'nacional' : 'ipm';
  const effective = status.effectiveProvider === 'nacional' ? 'nacional' : 'ipm';
  const startDate = form.nfseNacionalStartDate.trim();
  /** Pendências do billing (`status.missing`) viram erro no próprio campo enquanto ele estiver vazio. */
  const missingFieldErrors: Record<string, string> = {};
  for (const line of [...(status.missing ?? []), ...(activationBlocked ?? [])]) {
    const key = /^(\w+)/.exec(line)?.[1];
    if (key && key in form && String(form[key as keyof FiscalFormState]).trim() === '') {
      missingFieldErrors[key] = 'Obrigatório para ativar a NFS-e Nacional';
    }
  }
  const kit = { form, fieldErrors: { ...missingFieldErrors, ...fieldErrors }, onChange };

  return (
    <Box>
      <Card variant="outlined" sx={{ mb: 2, bgcolor: 'action.hover' }}>
        <CardContent>
          <Stack spacing={1.5}>
            <Typography variant="h5">Configuração Fiscal NFS-e (VOVER)</Typography>
            <Typography variant="body2" color="text.secondary">
              Configuração global de emissão usada pelo vover-billing. Escolha o provedor abaixo; o
              formulário exibido é o do provedor selecionado.
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip size="small" color="primary" label="Escopo global fiscal" />
              <Chip size="small" variant="outlined" label="Fonte: billingconfigs" />
              <Chip
                size="small"
                variant="outlined"
                label="Validar em homologação antes de produção"
              />
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {message && (
        <Alert sx={{ mb: 2 }} severity="success">
          {message}
        </Alert>
      )}
      {error && (
        <Alert sx={{ mb: 2 }} severity="error">
          {error}
        </Alert>
      )}

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 1.5 }}>
            Provedor de emissão
          </Typography>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
            <ToggleButtonGroup
              exclusive
              color="primary"
              value={view}
              onChange={(_, v: 'ipm' | 'nacional' | null) => {
                if (v) setForm(prev => ({ ...prev, nfseProvider: v }));
              }}
              aria-label="Provedor de emissão de NFS-e"
            >
              <ToggleButton value="ipm">IPM (prefeitura)</ToggleButton>
              <ToggleButton value="nacional">NFS-e Nacional</ToggleButton>
            </ToggleButtonGroup>
            <TextField
              label="Emitir pelo Nacional a partir de"
              type="date"
              value={form.nfseNacionalStartDate}
              onChange={onChange('nfseNacionalStartDate')}
              error={!!fieldErrors.nfseNacionalStartDate}
              helperText={
                fieldErrors.nfseNacionalStartDate ||
                'Marco da virada. Vazio = vale o provedor selecionado.'
              }
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 280 }}
            />
            {startDate && (
              <Button
                size="small"
                onClick={() => setForm(prev => ({ ...prev, nfseNacionalStartDate: '' }))}
              >
                Limpar data
              </Button>
            )}
          </Stack>

          {activationBlocked && (
            <Alert severity="error" sx={{ mt: 2 }}>
              <strong>Provedor e data NÃO foram gravados</strong> — a NFS-e Nacional ainda não pode
              ser ativada. Os demais dados foram salvos. Preencha o que falta (campos em vermelho no
              formulário da NFS-e Nacional) e salve novamente:
              <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5 }}>
                {activationBlocked.map(m => (
                  <li key={m}>{m}</li>
                ))}
              </Box>
            </Alert>
          )}

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 2 }}>
            <Chip
              size="small"
              color={effective === 'nacional' ? 'success' : 'default'}
              label={`Emitindo agora (salvo): ${PROVIDER_LABEL[effective]}`}
            />
            <Chip size="small" variant="outlined" label={`Editando: ${PROVIDER_LABEL[view]}`} />
          </Stack>

          {startDate ? (
            <Alert severity="info" sx={{ mt: 2 }}>
              A data <strong>prevalece</strong> sobre o provedor selecionado: o billing emite pelo{' '}
              <strong>
                IPM até a véspera de {new Date(`${startDate}T12:00:00`).toLocaleDateString('pt-BR')}
              </strong>{' '}
              e pela <strong>NFS-e Nacional a partir dessa data</strong>. Para o Nacional entrar em
              vigor, o formulário dele precisa estar completo.
            </Alert>
          ) : (
            <Alert severity="info" sx={{ mt: 2 }}>
              Sem data, emite pelo provedor selecionado acima. Preencha a data para agendar a virada
              automática do IPM para a NFS-e Nacional.
            </Alert>
          )}
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
            Provedor e data são gravados ao salvar o formulário abaixo, em qualquer um dos dois
            provedores.
          </Typography>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Box
            component="form"
            onSubmit={e => {
              e.preventDefault();
              void onSave();
            }}
          >
            <NfseCommonFields {...kit} />

            <Divider sx={{ my: 4 }} />

            {view === 'ipm' ? (
              <NfseIpmFields
                {...kit}
                ipmPasswordConfigured={ipmPasswordConfigured}
                onIpmPasswordChange={value => {
                  clearField('ipmPassword');
                  setIpmPasswordDirty(true);
                  setForm(prev => ({ ...prev, ipmPassword: value }));
                }}
              />
            ) : (
              <NfseNacionalFields
                {...kit}
                status={status}
                defaults={(rawConfig.nfseNacionalDefaults || {}) as Record<string, unknown>}
                onRestoreDefaults={() => {
                  const d = (rawConfig.nfseNacionalDefaults || {}) as Record<string, unknown>;
                  setForm(prev => ({
                    ...prev,
                    ...Object.fromEntries(Object.entries(d).map(([k, v]) => [k, String(v)]))
                  }));
                }}
                onToggleIbsCbs={checked =>
                  setForm(prev => ({ ...prev, nfseNacionalIncluirIbsCbs: checked }))
                }
              />
            )}

            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
              <Button type="submit" variant="contained" disabled={saving || loading}>
                {saving ? 'Salvando...' : 'Salvar configuração fiscal'}
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default NfseFiscalConfigPage;
